/**
 * The one object that owns the WebSocket (FE-ADR-066).
 *
 * Kept outside React so the connection's lifetime is explicit: `start()` once
 * the caller is signed in to an org, `stop()` on sign-out or org switch. A
 * connection token is minted for ONE membership, so a socket surviving an org
 * switch would be an isolation leak (F1) — teardown is a hard `disconnect()`
 * and a dropped client, never a re-auth of the live socket.
 *
 * Watchers are registered independently of the client: a component may ask
 * for a channel while the socket is down, and the wish is kept until it
 * unmounts. Wishes attach only once the socket is **connected**, and a `user:`
 * channel attaches only if it is the one this socket's own grant named — so
 * after an org switch the new socket never asks for the previous org's channel,
 * whatever order React re-renders in (the server would 403 it anyway). Each
 * watcher hears whether its channel is live, so a feature can tell "the socket
 * is up" from "my channel actually delivers".
 *
 * Reconnect/backoff is centrifuge-js's own (exponential with jitter); token
 * refresh is its `getToken` callback, so an expired JWT costs a round-trip
 * rather than a dropped connection.
 */
import { Centrifuge, UnauthorizedError, type Subscription } from 'centrifuge';

import type { ApiError } from '@/core/api';
import type {
  LiveHandler,
  PresenceHandlers,
  PublicationHandler,
  RealtimeStatus,
  Unsubscribe,
} from './types';

export interface ConnectionGrant {
  token: string;
  /** The caller's own channel in the org the token was minted for. */
  userChannel: string;
  /** That org's presence channel (backend ADR-107). */
  presenceChannel: string;
}

export interface RealtimeManagerOptions {
  url: string;
  getConnectionToken: () => Promise<ConnectionGrant>;
  getSubscriptionToken: (channel: string) => Promise<string>;
  onStatus?: (status: RealtimeStatus) => void;
  /** The current socket's own channel; null whenever the socket is stopped. */
  onUserChannel?: (channel: string | null) => void;
  /** The current socket's org presence channel; null whenever it is stopped. */
  onPresenceChannel?: (channel: string | null) => void;
}

interface Watcher {
  onData: PublicationHandler;
  onLive?: LiveHandler;
  presence?: PresenceHandlers;
}

/**
 * Some answers from a token endpoint are verdicts, not hiccups: 401/403 (the
 * caller may not have this, or is signed out) and the backend's own 503
 * `realtime_disabled` (switched off server-side). `UnauthorizedError` tells
 * centrifuge to stop retrying instead of polling the API on a loop that cannot
 * succeed; the bell's own poll carries on. Any other failure — a gateway 503
 * mid-deploy, a 500, the network — is retried with centrifuge's backoff.
 */
function isVerdict(error: ApiError | undefined): boolean {
  if (error?.status === 401 || error?.status === 403) return true;
  const code = (error?.raw as { code?: unknown } | undefined)?.code;
  return error?.status === 503 && code === 'realtime_disabled';
}

async function requestToken<T>(fetch: () => Promise<T>): Promise<T> {
  try {
    return await fetch();
  } catch (error) {
    const apiError = error as ApiError | undefined;
    if (isVerdict(apiError)) throw new UnauthorizedError(`token refused (${apiError?.status})`);
    throw error instanceof Error ? error : new Error('token request failed');
  }
}

const USER_PREFIX = 'user:';
const PRESENCE_PREFIX = 'presence:';

/** centrifuge-js may still emit 'error' after teardown (a refresh that was in
 *  flight); an EventEmitter with no 'error' listener throws, so keep one. */
const ignoreError = () => {};

export class RealtimeManager {
  private centrifuge: Centrifuge | null = null;
  /** What the app wants to watch — survives disconnects, not unmounts. */
  private watchers = new Map<string, Set<Watcher>>();
  private subscriptions = new Map<string, Subscription>();
  private live = new Set<string>();
  /** The `user:` channel this socket's grant named; null while stopped. */
  private userChannel: string | null = null;
  /** The `presence:` channel this socket's grant named; null while stopped. */
  private presenceChannel: string | null = null;
  private status: RealtimeStatus = 'disconnected';
  private readonly options: RealtimeManagerOptions;

  constructor(options: RealtimeManagerOptions) {
    this.options = options;
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  start(): void {
    if (this.centrifuge) return;

    const centrifuge = new Centrifuge(this.options.url, {
      getToken: async () => {
        const grant = await requestToken(this.options.getConnectionToken);
        // A token that arrives after this client was stopped belongs to the
        // previous org — never let it name the current channel.
        if (this.centrifuge !== centrifuge) throw new UnauthorizedError('stale');
        this.userChannel = grant.userChannel;
        this.presenceChannel = grant.presenceChannel;
        this.options.onUserChannel?.(grant.userChannel);
        this.options.onPresenceChannel?.(grant.presenceChannel);
        return grant.token;
      },
    });

    // A dropped socket delivers nothing, whatever its subscriptions report.
    centrifuge.on('connecting', () => {
      this.setStatus('connecting');
      this.dropAllLive();
    });
    centrifuge.on('connected', () => {
      this.setStatus('connected');
      for (const channel of this.watchers.keys()) this.attach(channel);
    });
    centrifuge.on('disconnected', () => {
      this.setStatus('disconnected');
      this.dropAllLive();
    });

    this.centrifuge = centrifuge;
    centrifuge.connect();
  }

  /** Tear the socket down. Registered watchers are kept — components own those. */
  stop(): void {
    for (const channel of [...this.subscriptions.keys()]) this.detach(channel);

    const centrifuge = this.centrifuge;
    this.centrifuge = null;
    if (centrifuge) {
      centrifuge.removeAllListeners();
      centrifuge.on('error', ignoreError);
      centrifuge.disconnect();
    }
    this.setStatus('disconnected');
    this.userChannel = null;
    this.presenceChannel = null;
    this.options.onUserChannel?.(null);
    this.options.onPresenceChannel?.(null);
  }

  /**
   * Watch a channel. Several components may watch the same one; they share one
   * subscription and the last to leave takes it down. `onLive` hears whether
   * the channel is currently delivering; `presence` hears its join/leave
   * events, when the server sends this member any.
   */
  subscribe<T>(
    channel: string,
    onData: PublicationHandler<T>,
    onLive?: LiveHandler,
    presence?: PresenceHandlers,
  ): Unsubscribe {
    const watcher: Watcher = { onData: onData as PublicationHandler, onLive, presence };
    const existing = this.watchers.get(channel);
    if (existing) {
      existing.add(watcher);
      onLive?.(this.live.has(channel));
    } else {
      this.watchers.set(channel, new Set([watcher]));
      if (this.status === 'connected') this.attach(channel);
    }

    return () => {
      const set = this.watchers.get(channel);
      if (!set) return;
      set.delete(watcher);
      if (set.size > 0) return;
      this.watchers.delete(channel);
      this.detach(channel);
    };
  }

  private attach(channel: string): void {
    const centrifuge = this.centrifuge;
    if (!centrifuge || this.subscriptions.has(channel)) return;
    // A socket can only ever read its own member's channel and its own org's
    // presence channel (F1).
    if (channel.startsWith(USER_PREFIX) && channel !== this.userChannel) return;
    if (channel.startsWith(PRESENCE_PREFIX) && channel !== this.presenceChannel) return;

    const subscription = centrifuge.newSubscription(channel, {
      getToken: () => requestToken(() => this.options.getSubscriptionToken(channel)),
    });

    this.subscriptions.set(channel, subscription);
    subscription.on('publication', (ctx) => {
      for (const w of this.watchers.get(channel) ?? []) w.onData(ctx.data);
    });
    subscription.on('join', (ctx) => {
      for (const w of this.watchers.get(channel) ?? []) w.presence?.onJoin?.(ctx.info.user);
    });
    subscription.on('leave', (ctx) => {
      for (const w of this.watchers.get(channel) ?? []) w.presence?.onLeave?.(ctx.info.user);
    });
    subscription.on('subscribed', () => this.setLive(channel, true));
    subscription.on('subscribing', () => this.setLive(channel, false));
    subscription.on('unsubscribed', () => this.setLive(channel, false));
    subscription.subscribe();
  }

  private detach(channel: string): void {
    const subscription = this.subscriptions.get(channel);
    if (!subscription) return;
    this.subscriptions.delete(channel);
    subscription.removeAllListeners();
    subscription.on('error', ignoreError);
    subscription.unsubscribe();
    this.centrifuge?.removeSubscription(subscription);
    this.setLive(channel, false);
  }

  private dropAllLive(): void {
    for (const channel of [...this.live]) this.setLive(channel, false);
  }

  private setLive(channel: string, live: boolean): void {
    if (this.live.has(channel) === live) return;
    if (live) this.live.add(channel);
    else this.live.delete(channel);
    for (const w of this.watchers.get(channel) ?? []) w.onLive?.(live);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }
}
